import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from '../utils/datetime';
import { formatWon } from '../utils/number';

/**
 * 명세서 PDF 위조 검증 — 공개 페이지 (인증 불필요).
 * URL 의 :id 가 있으면 자동 fetch. 없으면 입력창 표시.
 *
 * 사용자가 받은 PDF footer 의 발급번호와 화면 표시 정보를 직접 비교 →
 * 일치하지 않으면 위조 의심.
 */

interface VerifyData {
 audit_id: number;
 generated_at: string;
 content_sha256: string | null;
 transaction_hash: string;
 payer: { name: string | null; business: string | null };
 payee: { name: string | null; business: string | null };
 period: string | null;
 gross_amount: number;
 fee_amount: number;
 net_amount: number;
}

const won = (n: number) => formatWon(n);

const VerifyInvoice: React.FC = () => {
 const { id: routeId } = useParams<{ id?: string }>();
 const navigate = useNavigate();
 const [input, setInput] = useState(routeId ?? '');
 const [data, setData] = useState<VerifyData | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [loading, setLoading] = useState(false);

 const lookup = async (id: string) => {
  if (!id || !/^\d+$/.test(id)) {
   setError('숫자 형식의 발급번호를 입력해 주세요.');
   setData(null);
   return;
  }
  setLoading(true);
  setError(null);
  setData(null);
  try {
   const r = await fetch(`/market/invoices/verify/${id}`);
   const j = await r.json();
   if (!j.success) {
    setError(j.error || '검증 실패');
   } else {
    setData(j.data);
   }
  } catch (e: any) {
   setError(e.message || '서버 통신 실패');
  } finally {
   setLoading(false);
  }
 };

 useEffect(() => {
  if (routeId) lookup(routeId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [routeId]);

 const onSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  if (input) navigate(`/verify-invoice/${input.trim()}`);
 };

 return (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
   <div className="bg-white max-w-2xl w-full p-6 shadow-lg">
    <h1 className="text-2xl font-bold text-gray-800 mb-1">거래 명세서 검증</h1>
    <p className="text-sm text-gray-500 mb-4">
     PDF 푸터의 발급번호를 입력해 위조 여부를 확인하세요. 표시된 정보가 본인의 PDF 와 일치하지 않으면 위조 가능성이 있습니다.
    </p>

    <form onSubmit={onSubmit} className="flex gap-2 mb-4">
     <input
      value={input}
      onChange={e => setInput(e.target.value)}
      placeholder="발급번호 (예: 123)"
      className="flex-1 px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
     />
     <button
      type="submit"
      disabled={loading}
      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
     >{loading ? '조회 중...' : '검증'}</button>
    </form>

    {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm mb-3">{error}</div>}

    {data && (
     <div className="border border-gray-200 p-4 bg-gray-50">
      <div className="flex items-baseline gap-2 mb-3 pb-2 border-b border-gray-200">
       <span className="text-xs text-gray-500">발급번호</span>
       <span className="text-lg font-bold text-gray-800">#{data.audit_id}</span>
       <span className="ml-auto bg-green-100 text-green-700 text-xs px-2 py-0.5">발급 기록 확인됨</span>
      </div>

      <dl className="space-y-2 text-sm">
       <Row label="발급일시" value={formatDateTime(data.generated_at)} />
       <Row label="거래번호" value={<code className="text-[11px] break-all">{data.transaction_hash}</code>} />
       <Row label="기간" value={data.period ?? '-'} />
       <Row label="지급 측" value={`${data.payer.name ?? '-'}${data.payer.business ? ` (${data.payer.business})` : ''}`} />
       <Row label="수령 측" value={`${data.payee.name ?? '-'}${data.payee.business ? ` (${data.payee.business})` : ''}`} />
       <Row label="총액" value={won(data.gross_amount)} />
       <Row label="수수료" value={won(data.fee_amount)} />
       <Row label="수령액" value={won(data.net_amount)} highlight />
       {data.content_sha256 && (
        <Row label="본문 해시 (SHA-256)" value={
         <code className="text-[10px] break-all text-gray-600">{data.content_sha256}</code>
        } />
       )}
      </dl>

      <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-xs">
       위 정보가 본인이 받은 PDF 의 항목과 일치하는지 직접 비교해 주세요. 한 항목이라도
       다르면 위조 가능성이 있습니다.
      </div>
     </div>
    )}

    <div className="mt-6 text-center text-xs text-gray-400">
     <a href="/" className="hover:underline">메인으로</a>
    </div>
   </div>
  </div>
 );
};

const Row: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({ label, value, highlight }) => (
 <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
  <dt className="text-gray-500 sm:w-32 sm:flex-shrink-0">{label}</dt>
  <dd className={`flex-1 ${highlight ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>{value}</dd>
 </div>
);

export default VerifyInvoice;
